import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Servicio para manejar operaciones de Declaraciones Juradas
 * Compatible con MCP (Model Context Protocol)
 */
class DeclaracionesJuradasService {
    _getHeaders(token) {
        return {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
        };
    }

    _handleError(error, defaultMessage) {
        if (error.response) {
            throw new Error(error.response.data.message || defaultMessage);
        } else if (error.request) {
            throw new Error('No se pudo conectar con el servidor');
        } else {
            throw new Error(error.message || defaultMessage);
        }
    }

    // ========== DECLARACIONES JURADAS (CRUD GENERAL) ==========

    async getDeclaracionesJuradas(year, id_company, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/declaracion-renta/juradas`,
                {
                    params: { year, id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data.data || [];
        } catch (error) {
            this._handleError(error, 'Error al obtener declaraciones juradas');
        }
    }

    async createDeclaracionJurada(data, token) {
        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/declaracion-renta/juradas`,
                data,
                { headers: this._getHeaders(token) }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al crear declaración jurada');
        }
    }

    async updateDeclaracionJurada(id, data, token) {
        try {
            const response = await axios.put(
                `${API_BASE_URL}/api/declaracion-renta/juradas/${id}`,
                data,
                { headers: this._getHeaders(token) }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al actualizar declaración jurada');
        }
    }

    async deleteDeclaracionJurada(id, id_company, token) {
        try {
            const response = await axios.delete(
                `${API_BASE_URL}/api/declaracion-renta/juradas/${id}`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al eliminar declaración jurada');
        }
    }

    async getTiposDeclaracionJurada(token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/declaracion-renta/juradas/tipos`,
                { headers: this._getHeaders(token) }
            );
            return response.data.data || [];
        } catch (error) {
            this._handleError(error, 'Error al obtener tipos de declaración jurada');
        }
    }

    // ========== DJ1835 - BIENES RAÍCES ARRENDADOS ==========

    async getBienesRaicesArrendados(idDeclaracion, id_company, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/declaracion-renta/dj1835/${idDeclaracion}`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data.data || [];
        } catch (error) {
            this._handleError(error, 'Error al obtener bienes raíces arrendados');
        }
    }

    async createBienRaizArrendado(idDeclaracion, data, id_company, token) {
        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/declaracion-renta/dj1835/${idDeclaracion}`,
                data,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al crear bien raíz arrendado');
        }
    }

    async updateBienRaizArrendado(id, data, id_company, token) {
        try {
            const response = await axios.put(
                `${API_BASE_URL}/api/declaracion-renta/dj1835/${id}`,
                data,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al actualizar bien raíz arrendado');
        }
    }

    async deleteBienRaizArrendado(id, id_company, token) {
        try {
            const response = await axios.delete(
                `${API_BASE_URL}/api/declaracion-renta/dj1835/${id}`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al eliminar bien raíz arrendado');
        }
    }

    async downloadDJ1835CSV(idDeclaracion, id_company, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/declaracion-renta/dj1835/${idDeclaracion}/csv`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token),
                    responseType: 'blob'
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al descargar CSV');
        }
    }

    async validateDJ1835Data(idDeclaracion, id_company, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/declaracion-renta/dj1835/${idDeclaracion}/validate`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al validar datos');
        }
    }

    async downloadAndSaveDJ1835CSV(idDeclaracion, id_company, rutEmpresa, year, token) {
        try {
            const blob = await this.downloadDJ1835CSV(idDeclaracion, id_company, token);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `DJ1835_${rutEmpresa}_${year}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            this._handleError(error, 'Error al descargar y guardar CSV');
        }
    }

    // ========== DJ1879 - HONORARIOS Y RETENCIONES ==========

    async getHonorarios(idDeclaracion, id_company, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/declaracion-renta/dj1879/${idDeclaracion}`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data.data || [];
        } catch (error) {
            this._handleError(error, 'Error al obtener honorarios');
        }
    }

    async createHonorario(idDeclaracion, data, id_company, token) {
        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/declaracion-renta/dj1879/${idDeclaracion}`,
                data,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al crear honorario');
        }
    }

    async updateHonorario(id, data, id_company, token) {
        try {
            const response = await axios.put(
                `${API_BASE_URL}/api/declaracion-renta/dj1879/${id}`,
                data,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al actualizar honorario');
        }
    }

    async deleteHonorario(id, id_company, token) {
        try {
            const response = await axios.delete(
                `${API_BASE_URL}/api/declaracion-renta/dj1879/${id}`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al eliminar honorario');
        }
    }

    async downloadDJ1879CSV(idDeclaracion, id_company, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/declaracion-renta/dj1879/${idDeclaracion}/csv`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token),
                    responseType: 'blob'
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al descargar CSV');
        }
    }

    async validateDJ1879Data(idDeclaracion, id_company, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/declaracion-renta/dj1879/${idDeclaracion}/validate`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al validar datos');
        }
    }

    async downloadDJ1879PDF(idDeclaracion, id_company, rutEmpresa, year, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/declaracion-renta/dj1879/${idDeclaracion}/pdf`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token),
                    responseType: 'blob'
                }
            );
            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = `DJ1879_${rutEmpresa}_${year}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            this._handleError(error, 'Error al descargar PDF');
        }
    }

    async downloadAndSaveDJ1879CSV(idDeclaracion, id_company, rutEmpresa, year, token) {
        try {
            const blob = await this.downloadDJ1879CSV(idDeclaracion, id_company, token);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `DJ1879_${rutEmpresa}_${year}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            this._handleError(error, 'Error al descargar y guardar CSV');
        }
    }

    // ========== DJ1887 - RENTAS ART. 42 N°1 (SUELDOS) ==========

    async getRemuneraciones(idDeclaracion, id_company, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/declaracion-renta/dj1887/${idDeclaracion}`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data.data || [];
        } catch (error) {
            this._handleError(error, 'Error al obtener remuneraciones');
        }
    }

    async createRemuneracion(idDeclaracion, data, id_company, token) {
        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/declaracion-renta/dj1887/${idDeclaracion}`,
                data,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al crear remuneración');
        }
    }

    async updateRemuneracion(id, data, id_company, token) {
        try {
            const response = await axios.put(
                `${API_BASE_URL}/api/declaracion-renta/dj1887/${id}`,
                data,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al actualizar remuneración');
        }
    }

    async deleteRemuneracion(id, id_company, token) {
        try {
            const response = await axios.delete(
                `${API_BASE_URL}/api/declaracion-renta/dj1887/${id}`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al eliminar remuneración');
        }
    }

    async downloadDJ1887CSV(idDeclaracion, id_company, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/declaracion-renta/dj1887/${idDeclaracion}/csv`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token),
                    responseType: 'blob'
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al descargar CSV');
        }
    }

    async validateDJ1887Data(idDeclaracion, id_company, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/declaracion-renta/dj1887/${idDeclaracion}/validate`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al validar datos');
        }
    }

    async downloadAndSaveDJ1887CSV(idDeclaracion, id_company, rutEmpresa, year, token) {
        try {
            const blob = await this.downloadDJ1887CSV(idDeclaracion, id_company, token);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `DJ1887_${rutEmpresa}_${year}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            this._handleError(error, 'Error al descargar y guardar CSV');
        }
    }

    async downloadDJ1887PDF(idDeclaracion, id_company, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/declaracion-renta/dj1887/${idDeclaracion}/pdf`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token),
                    responseType: 'blob'
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al descargar PDF');
        }
    }

    async downloadAndSaveDJ1887PDF(idDeclaracion, id_company, rutEmpresa, year, token) {
        try {
            const blob = await this.downloadDJ1887PDF(idDeclaracion, id_company, token);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `DJ1887_${rutEmpresa}_${year}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            this._handleError(error, 'Error al descargar y guardar PDF');
        }
    }

    // ========== DJ1948 - RETIROS Y DIVIDENDOS ==========

    async downloadDJ1948CSV(id_company, anio, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/socios-dividendos/csv-dj1948`,
                {
                    params: { id_company, anio },
                    headers: this._getHeaders(token),
                    responseType: 'blob'
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al descargar CSV DJ1948');
        }
    }

    async downloadAndSaveDJ1948CSV(id_company, rutEmpresa, anio, token) {
        try {
            const blob = await this.downloadDJ1948CSV(id_company, anio, token);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `DJ1948_${rutEmpresa}_${anio}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            this._handleError(error, 'Error al descargar y guardar CSV DJ1948');
        }
    }
}

const declaracionesJuradasService = new DeclaracionesJuradasService();
export default declaracionesJuradasService;
